import { Button, Icon, makeStyles, Theme } from '@material-ui/core';
import { Entity } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties } from 'react';


const useStyle = makeStyles((theme: Theme) => {

    const arrowSize = 18;
    const dividerSize = 25;

    return {

        root: {
            flex: 1,
        },

        breadcrumbsContainer: {
            backgroundColor: theme.palette.background.paper,
            width          : 'fit-content',
            boxShadow      : theme.shadows[2],
            display        : 'flex',
            alignItems     : 'center',
        },

        homeButton: {
            paddingLeft : 0,
            paddingRight: 0,
            minWidth    : 'auto',
            '& .icon'   : {
                margin: theme.spacing(0, 1),
            },
        },

        homeIcon: {
            paddingLeft : 0,
            paddingRight: 0,
            minWidth    : 'auto',
            display     : 'flex',
            alignItems  : 'center',
            '& .icon'   : {
                margin: theme.spacing(0, 1),
            },
        },

        breadcrumbSeparatorAndBreadcrumb: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
        },

        breadcrumbSeparator: {
            width        : dividerSize,
            height       : dividerSize,
            marginTop    : -dividerSize,
            marginRight  : -dividerSize,
            borderTop    : `2px solid ${theme.palette.grey[300]}`,
            borderRight  : `2px solid ${theme.palette.grey[300]}`,
            transform    : 'translate3D(-50%, 50%, 0) rotateZ(45deg)',
            pointerEvents: 'none',
        },

        breadcrumb: {
            display   : 'flex',
            marginLeft: arrowSize,
        },

        breadcrumbArrow: {
            display   : 'flex',
            alignItems: 'center',
            width     : 'fit-content',

            '&.current': {
                pointerEvents  : 'none',
                backgroundColor: theme.palette.primary.main,
                '&:before'     : {
                    content     : '""',
                    display     : 'inline-block',
                    borderTop   : `${arrowSize}px solid ${theme.palette.primary.main}`,
                    borderBottom: `${arrowSize}px solid ${theme.palette.primary.main}`,
                    borderLeft  : `${arrowSize}px solid transparent`,
                    marginLeft  : -arrowSize,
                },
                '&:after': {
                    content     : '""',
                    display     : 'inline-block',
                    borderTop   : `${arrowSize}px solid transparent`,
                    borderBottom: `${arrowSize}px solid transparent`,
                    borderLeft  : `${arrowSize}px solid ${theme.palette.primary.main}`,
                    marginRight : -arrowSize,
                },
                '& $breadcrumbButton': {
                    color: theme.palette.getContrastText(theme.palette.primary.main),
                },
            },
        },

        breadcrumbButton: {
            textTransform: 'none',
            borderRadius : 0,
            padding      : 0,
            color        : theme.palette.text.primary,
            '& .label'   : {
                paddingLeft : theme.spacing(1),
                paddingRight: theme.spacing(1),
                whiteSpace  : 'nowrap',
            },
        },

        [theme.breakpoints.down('sm')]: {
            // On mobile screen, hide all breadcrumb EXCEPT the last 2 breadcrumbs
            breadcrumbSeparatorAndBreadcrumb: {
                display                   : 'none',
                '&:nth-last-child(-n + 2)': {
                    display: 'flex',
                },
            },
        },
    };
}, { name: 'EntityBreadcrumbBar', index: 1 });



interface EntityBreadcrumbProps {
    readonly entity: Entity;
    readonly isCurrentEntity: boolean;
    readonly onClick?: ((entity: Entity)=> void) | undefined;
}


/**
 * The UI for 1 breadcrumb. For now it will just going to be a button. We will style it once we decide which version of the breadcrumb
 * style from the mock we want to use
 * @param props
 * @returns
 */
const EntityBreadcrumb: React.FC<EntityBreadcrumbProps> = (props: EntityBreadcrumbProps) => {
    const classes = useStyle();

    return (
        <div className={classes.breadcrumb}>
            <div className={clsx(classes.breadcrumbArrow, props.isCurrentEntity && 'current')}>
                <Button
                    className={clsx(classes.breadcrumbButton)}
                    onClick={() => {
                        if (props.onClick) {
                            props.onClick(props.entity);
                        }
                    }}
                >
                    <div className="label">
                        {props.entity.displaySettings.name}
                    </div>
                </Button>
            </div>
        </div>
    );
};



export interface EntityBreadcrumbBarProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    readonly onGoHomeClick?: ()=> void | undefined;

    readonly entitiesBreadcrumbs: readonly Entity[];
    readonly onGotoEntity: (entity: Entity)=> void;
}


export const EntityBreadcrumbBar: React.FC<EntityBreadcrumbBarProps> = (props: EntityBreadcrumbBarProps) => {

    const classes = useStyle();

    return (
        <div className={clsx(classes.root, props.className)} style={props.style}>
            <div className={classes.breadcrumbsContainer}>
                {/**
                 * If there is a 'goHomeClick' handler then make the Home a button. Else, just show an icon.
                 */}
                {props.onGoHomeClick ? (
                    <Button
                        className={clsx(classes.homeButton)}
                        variant="text"
                        onClick={props.onGoHomeClick}
                    >
                        <Icon className="icon">account_tree</Icon>
                    </Button>
                ) : (
                    <span className={clsx(classes.homeIcon)}>
                        <Icon className="icon">account_tree</Icon>
                    </span>
                )}
                {props.entitiesBreadcrumbs.map((entity, index) => {
                    return (
                        <div className={classes.breadcrumbSeparatorAndBreadcrumb} key={entity.entityId}>
                            {index < props.entitiesBreadcrumbs.length - 1 && (
                                <div className={classes.breadcrumbSeparator} />
                            )}
                            <EntityBreadcrumb
                                key={entity.entityId}
                                entity={entity}
                                isCurrentEntity={index === props.entitiesBreadcrumbs.length - 1}
                                onClick={index < props.entitiesBreadcrumbs.length - 1 ? props.onGotoEntity : undefined}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
